import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Box, Typography, Chip, Alert } from "@mui/material";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { formatDate, formatTime } from "utils/formatters";
import {
  getContractItemHistory,
  getChangeTypeDisplayName,
  getChangeTypeIcon,
  getChangeTypeColor,
} from "../../services/contractItemHistory.service";

// Helper function to format time only (HH:mm:ss)
// const formatTimeOnly = (dateTimeString) => {
//   if (!dateTimeString) return "";
//   const date = new Date(dateTimeString);
//   return date.toLocaleTimeString("vi-VN", {
//     hour: "2-digit",
//     minute: "2-digit",
//     second: "2-digit",
//     hour12: false,
//   });
// };

const ContractItemHistory = ({ contractId }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Group history data by date for master-detail view
  const groupHistoryByDate = (historyData) => {
    if (!Array.isArray(historyData) || historyData.length === 0) {
      return [];
    }

    // Group by date only (YYYY-MM-DD)
    const groupedData = historyData.reduce((groups, item) => {
      const date = new Date(item.changedAt);
      const dateKey = date.toISOString().split("T")[0]; // Get YYYY-MM-DD only
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(item);
      return groups;
    }, {});

    // Convert to array and sort by date (newest first)
    const sortedGroups = Object.entries(groupedData)
      .sort(([a], [b]) => new Date(b) - new Date(a))
      .map(([dateKey, items]) => {
        // Sort items within the day by time (newest first)
        const sortedItems = items.sort(
          (a, b) => new Date(b.changedAt) - new Date(a.changedAt)
        );

        // Create master row
        const masterRow = {
          id: `group_${dateKey}`,
          isGroup: true,
          dateKey,
          changeCount: sortedItems.length,
          changedAt: dateKey,
          changeType: `${sortedItems.length} thay đổi`,
          itemCode: "",
          itemName: "",
          fieldDisplayName: "",
          oldValue: "",
          newValue: "",
          changedBy: "",
          subRows: sortedItems.map((item, index) => ({
            ...item,
            id: `${dateKey}_${index}`,
            isGroup: false,
          })),
        };

        return masterRow;
      });

    return sortedGroups;
  };

  // Define columns for Material React Table
  const columns = useMemo(
    () => [
      {
        accessorKey: "changedAt",
        header: "Thời gian",
        size: 150,
        Cell: ({ row, cell }) => {
          if (row.original.isGroup) {
            return (
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                {formatDate(cell.getValue())}
              </Typography>
            );
          }
          // return formatTimeOnly(cell.getValue());
          return formatTime(cell.getValue());
        },
        enableGlobalFilter: true,
      },
      {
        accessorKey: "changeType",
        header: "Loại thay đổi",
        size: 130,
        enableSorting: false,
        enableGlobalFilter: true,
        accessorFn: (row) => {
          if (row.isGroup) {
            return `${row.changeCount} thay đổi`;
          }
          // Return both original value and display name for search
          const displayName = getChangeTypeDisplayName(row.changeType);
          return `${row.changeType} ${displayName}`;
        },
        Cell: ({ row }) => {
          if (row.original.isGroup) {
            return (
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                {row.original.changeCount} thay đổi
              </Typography>
            );
          }

          const changeType = row.original.changeType;
          return (
            <Chip
              icon={
                <span style={{ fontSize: "12px" }}>
                  {getChangeTypeIcon(changeType)}
                </span>
              }
              label={getChangeTypeDisplayName(changeType)}
              size="small"
              sx={{
                bgcolor: getChangeTypeColor(changeType) + "20",
                color: getChangeTypeColor(changeType),
                border: `1px solid ${getChangeTypeColor(changeType)}40`,
              }}
            />
          );
        },
      },
      {
        accessorKey: "itemCode",
        header: "Mã hạng mục",
        size: 120,
        enableSorting: false,
        enableGlobalFilter: true,
        Cell: ({ row, cell }) => {
          if (row.original.isGroup) {
            return null;
          }
          return cell.getValue();
        },
      },
      {
        accessorKey: "itemName",
        header: "Tên hạng mục",
        size: 200,
        enableSorting: false,
        enableGlobalFilter: true,
        Cell: ({ row, cell }) => {
          if (row.original.isGroup) {
            return null;
          }
          return (
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "200px",
              }}
            >
              {cell.getValue()}
            </Typography>
          );
        },
      },
      {
        accessorKey: "fieldDisplayName",
        header: "Trường thay đổi",
        size: 150,
        enableSorting: false,
        enableGlobalFilter: true,
        Cell: ({ row, cell }) => {
          if (row.original.isGroup) {
            return null;
          }
          return cell.getValue() || "-";
        },
      },
      {
        accessorKey: "oldValue",
        header: "Giá trị cũ",
        size: 150,
        enableSorting: false,
        Cell: ({ row, cell }) => {
          if (row.original.isGroup) {
            return null;
          }
          return cell.getValue() || "-";
        },
        enableGlobalFilter: true,
      },
      {
        accessorKey: "newValue",
        header: "Giá trị mới",
        size: 150,
        enableSorting: false,
        Cell: ({ row, cell }) => {
          if (row.original.isGroup) {
            return null;
          }
          return cell.getValue() || "-";
        },
        enableGlobalFilter: true,
      },
      {
        accessorKey: "changedBy",
        header: "Người thay đổi",
        size: 120,
        enableSorting: false,
        enableGlobalFilter: true,
        Cell: ({ row, cell }) => {
          if (row.original.isGroup) {
            return null;
          }
          return cell.getValue();
        },
      },
    ],
    []
  );

  // Fetch data
  const fetchHistory = useCallback(async () => {
    if (!contractId) return;

    setLoading(true);
    setError(null);
    try {
      // Fetch all data at once for client-side filtering/sorting
      const response = await getContractItemHistory(contractId, 1, 1000);
      const groupedData = groupHistoryByDate(response.items || []);
      setData(groupedData);
    } catch (err) {
      setError("Không thể tải lịch sử hạng mục: " + err.message);
      console.error("Error fetching contract item history:", err);
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Material React Table configuration
  const table = useMaterialReactTable({
    columns,
    data,
    enableColumnActions: false,
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableGrouping: false,
    enablePinning: true,
    enableRowSelection: false,
    enableColumnResizing: true,
    enableExpanding: true,
    enableExpandAll: false,
    columnFilterDisplayMode: "popover",
    paginationDisplayMode: "pages",
    positionToolbarAlertBanner: "bottom",
    enableGlobalFilter: true,
    globalFilterFn: "includesString",
    enableSorting: false, // Disable sorting to preserve grouping
    enablePagination: true,
    getSubRows: (row) => row.subRows || [],
    filterFromLeafRows: true,
    state: {
      isLoading: loading,
    },
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: true,
      pagination: {
        pageIndex: 0,
        pageSize: 25,
      },
      expanded: false, // Start with all groups collapsed
    },
    muiSearchTextFieldProps: {
      placeholder: "Tìm kiếm lịch sử hạng mục...",
      sx: { minWidth: "300px" },
      variant: "outlined",
    },
    muiTopToolbarProps: {
      sx: {
        backgroundColor: "transparent",
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        border: "1px solid #e0e0e0",
      },
    },
    muiTableProps: {
      sx: {
        tableLayout: "fixed",
      },
    },
    muiTableHeadCellProps: {
      sx: {
        fontWeight: "bold",
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: row.original.isGroup ? "#e3f2fd" : "transparent",
        "&:hover": {
          backgroundColor: row.original.isGroup ? "#bbdefb" : "#f9f9f9",
        },
        fontWeight: row.original.isGroup ? "bold" : "normal",
      },
    }),
    muiPaginationProps: {
      color: "primary",
      shape: "rounded",
      variant: "outlined",
    },
    localization: {
      actions: "Hành động",
      and: "và",
      cancel: "Hủy",
      changeFilterMode: "Thay đổi chế độ lọc",
      changeSearchMode: "Thay đổi chế độ tìm kiếm",
      clearFilter: "Xóa bộ lọc",
      clearSearch: "Xóa tìm kiếm",
      clearSort: "Xóa sắp xếp",
      clickToCopy: "Nhấp để sao chép",
      collapse: "Thu gọn",
      collapseAll: "Thu gọn tất cả",
      columnActions: "Hành động cột",
      copiedToClipboard: "Đã sao chép vào clipboard",
      dropToGroupBy: "Kéo để nhóm theo {column}",
      edit: "Chỉnh sửa",
      expand: "Mở rộng",
      expandAll: "Mở rộng tất cả",
      filterArrIncludes: "Bao gồm",
      filterArrIncludesAll: "Bao gồm tất cả",
      filterArrIncludesSome: "Bao gồm một số",
      filterBetween: "Giữa",
      filterBetweenInclusive: "Giữa (bao gồm)",
      filterByColumn: "Lọc theo {column}",
      filterContains: "Chứa",
      filterEmpty: "Trống",
      filterEndsWith: "Kết thúc bằng",
      filterEquals: "Bằng",
      filterEqualsString: "Bằng",
      filterFuzzy: "Mờ",
      filterGreaterThan: "Lớn hơn",
      filterGreaterThanOrEqualTo: "Lớn hơn hoặc bằng",
      filterInNumberRange: "Trong khoảng",
      filterIncludesString: "Bao gồm",
      filterIncludesStringSensitive: "Bao gồm (phân biệt hoa thường)",
      filterLessThan: "Nhỏ hơn",
      filterLessThanOrEqualTo: "Nhỏ hơn hoặc bằng",
      filterMode: "Chế độ lọc: {filterType}",
      filterNotEmpty: "Không trống",
      filterNotEquals: "Không bằng",
      filterStartsWith: "Bắt đầu bằng",
      filterWeakEquals: "Bằng",
      filteringByColumn:
        "Đang lọc theo {column} - {filterType} - {filterValue}",
      goToFirstPage: "Trang đầu",
      goToLastPage: "Trang cuối",
      goToNextPage: "Trang sau",
      goToPreviousPage: "Trang trước",
      grab: "Lấy",
      groupByColumn: "Nhóm theo {column}",
      groupedBy: "Đã nhóm theo ",
      hideAll: "Ẩn tất cả",
      hideColumn: "Ẩn cột {column}",
      max: "Tối đa",
      min: "Tối thiểu",
      move: "Di chuyển",
      noRecordsToDisplay: "Không có dữ liệu để hiển thị",
      noResultsFound: "Không tìm thấy kết quả",
      of: "của",
      or: "hoặc",
      pinToLeft: "Ghim bên trái",
      pinToRight: "Ghim bên phải",
      resetColumnSize: "Đặt lại kích thước cột",
      resetOrder: "Đặt lại thứ tự",
      rowActions: "Hành động dòng",
      rowNumber: "Số dòng",
      rowNumbers: "Số dòng",
      rowsPerPage: "Dòng mỗi trang",
      save: "Lưu",
      search: "Tìm kiếm",
      select: "Chọn",
      selectedCountOfRowCountRowsSelected:
        "{selectedCount} của {rowCount} dòng đã chọn",
      showAll: "Hiển thị tất cả",
      showAllColumns: "Hiển thị tất cả cột",
      showHideColumns: "Hiển thị/Ẩn cột",
      showHideFilters: "Hiển thị/Ẩn bộ lọc",
      showHideSearch: "Hiển thị/Ẩn tìm kiếm",
      sortByColumnAsc: "Sắp xếp theo {column} tăng dần",
      sortByColumnDesc: "Sắp xếp theo {column} giảm dần",
      sortedByColumnAsc: "Đã sắp xếp theo {column} tăng dần",
      sortedByColumnDesc: "Đã sắp xếp theo {column} giảm dần",
      thenBy: ", sau đó theo ",
      toggleDensity: "Chuyển đổi mật độ",
      toggleFullScreen: "Chuyển đổi toàn màn hình",
      toggleSelectAll: "Chuyển đổi chọn tất cả",
      toggleSelectRow: "Chuyển đổi chọn dòng",
      toggleVisibility: "Chuyển đổi hiển thị",
      ungroupByColumn: "Bỏ nhóm theo {column}",
      unpin: "Bỏ ghim",
      unpinAll: "Bỏ ghim tất cả",
      unsorted: "Chưa sắp xếp",
    },
  });

  if (!contractId) {
    return (
      <Alert severity="warning">
        Vui lòng chọn một hợp đồng để xem lịch sử hạng mục.
      </Alert>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <MaterialReactTable table={table} />
    </Box>
  );
};

export default ContractItemHistory;
